import { gql } from "@apollo/client";

// queries

export const GET_EMPLOYEES = gql`
	query getEmployees(
		$columns: JSON
		$page: String
		$pageSize: String
		$filters: [[FilterItem]]
		$sorts: [EmployeesSort]
	) {
		employees(columns: $columns, page: $page, pageSize: $pageSize, filters: $filters, sorts: $sorts)
	}
`;

export const GET_CUSTOM_FIELDS = gql`
	query Query($name: String) {
		fields(name_contains: $name) {
			_id
			name
			type
			vis
		}
	}
`;

export const GET_SELECT_FIELD_OPTIONS = gql`
	query Query($name: String!) {
		fields(name: $name) {
			_id
			name
			options
		}
	}
`;

export const GET_GROUPS = gql`
	query Query {
		groups {
			_id
			name
		}
	}
`;

export const GET_REVIEW_TEMPLATES = gql`
	query Query($name: String) {
		reviewTemplates(name_contains: $name) {
			_id
			name
		}
	}
`;

export const GET_REVIEW_CYCLES = gql`
	query Query {
		uniqueReviewCycleNames {
			_id
			name
		}
	}
`;

export const GET_REVIEW_FORMS = gql`
	query Query {
		uniqueReviewFormNames {
			_id
			name
		}
	}
`;

export const GET_UNIQUE_QUESTIONS = gql`
	query GetUniqueQuestions($questionGroupingSessionID: ObjectID, $filters: [[FilterItem]]) {
		uniqueReviewReportQuestions(questionGroupingSessionID: $questionGroupingSessionID, filters: $filters) {
			_id
			question
			reviewFormName
			reviewFormID
			questionType
			questionHash
			min
			max
		}
	}
`;

export const GET_NON_UNIQUE_QUESTIONS = gql`
	query GetNonUniqueQuestions($questionGroupingSessionID: ObjectID, $filters: [[FilterItem]]) {
		nonUniqueReviewReportQuestions(questionGroupingSessionID: $questionGroupingSessionID, filters: $filters) {
			_id
			question
			reviewFormName
			reviewFormID
			questionType
			questionHash
			min
			max
		}
	}
`;

export const GET_UNIQUE_SURVEY_QUESTIONS = gql`
	query GetUniqueSurveyQuestions($questionGroupingSessionID: ObjectID, $filters: [[FilterItem]]) {
		uniqueSurveyQuestions(questionGroupingSessionID: $questionGroupingSessionID, filters: $filters) {
			_id
			question
			surveyName
			surveyID
			questionType
			questionHash
			min
			max
		}
	}
`;

export const GET_NON_UNIQUE_SURVEY_QUESTIONS = gql`
	query GetNonUniqueSurveyQuestions($questionGroupingSessionID: ObjectID, $filters: [[FilterItem]]) {
		nonUniqueSurveyQuestions(questionGroupingSessionID: $questionGroupingSessionID, filters: $filters) {
			_id
			question
			surveyName
			surveyID
			questionType
			questionHash
			min
			max
		}
	}
`;

export const GET_QUESTION_GROUPING_SESSION = gql`
	query QuestionGroupingSession($groupingSessionID: ObjectID) {
		questionGroupingSession(id: $groupingSessionID) {
			_id
			employeeQueryID
			reviewQueryID
			selectedQuestions {
				_id
				name
				questions
				is_grouped
				summary_function
			}
		}
	}
`;

export const GET_SAVED_QUERY = gql`
	query Query($queryID: ObjectID!) {
		savedQuery(queryID: $queryID) {
			_id
			name
			description
			type
			question_grouping_session_id
			filters {
				field
				type
				comparison
				category
				value
			}
			tags
			createdAt
			createdBy {
				_id
				name
			}
			updatedAt
			selectedColumns
			quickFilter {
				type
				value
			}
			sorts {
				name
				direction
				type
				fieldPath
			}
			columnMetadata
			summarizeBy
			aggregationOperations
			isSummarizeRowsView
			showAllAggregationColumns
			summarizeByMetadata
			visualizeOptions
			pageSize
			sharedWith
			deletedAt
		}
	}
`;

export const GET_SAVED_QUERIES = gql`
	query Query(
		$search: Search
		$sort: AnalyticsReportsSort
		$pageNumber: Int
		$pageSize: Int
		$type: [String]
		$sharedWith: [String]
	) {
		savedQueries(
			search: $search
			sort: $sort
			pageNumber: $pageNumber
			pageSize: $pageSize
			type: $type
			sharedWith: $sharedWith
		)
	}
`;

export const GET_RAW_REVIEW_REPORT_DATA = gql`
	query GetReviewReportData(
		$questionGroupingSessionID: ObjectID!
		$columns: JSON
		$pageNumber: String
		$pageSize: String
		$filters: [[FilterItem]]
		$sorts: [ReviewReportsSort]
		$summarizeBy: [ReviewReportsSummarizeBy]
		$aggregationOperations: [ReviewReportsAggregationOperation]
	) {
		reviewReports(
			questionGroupingSessionID: $questionGroupingSessionID
			columns: $columns
			filters: $filters
			pageNumber: $pageNumber
			pageSize: $pageSize
			sorts: $sorts
			summarizeBy: $summarizeBy
			aggregationOperations: $aggregationOperations
		)
	}
`;

export const GET_GOAL_REPORT_DATA_COUNT = gql`
	query GetGoalReportDataCount(
		$columns: JSON
		$filters: [[FilterItem]]
		$sorts: [GoalReportsSort]
		$summarizeBy: [GoalReportsSummarizeBy]
		$aggregationOperations: [GoalReportsAggregationOperation]
	) {
		goalReportsCount(
			columns: $columns
			filters: $filters
			sorts: $sorts
			summarizeBy: $summarizeBy
			aggregationOperations: $aggregationOperations
		)
	}
`;

export const GET_EMPLOYEE_REPORT_DATA_COUNT = gql`
	query GetEmployeeReportDataCount(
		$columns: JSON
		$filters: [[FilterItem]]
		$sorts: [ReviewReportsSort]
		$summarizeBy: [ReviewReportsSummarizeBy]
		$aggregationOperations: [ReviewReportsAggregationOperation]
	) {
		employeeReportsCount(
			columns: $columns
			filters: $filters
			sorts: $sorts
			summarizeBy: $summarizeBy
			aggregationOperations: $aggregationOperations
		)
	}
`;

export const GET_REVIEW_REPORT_DATA_COUNT = gql`
	query GetReviewReportDataCount(
		$questionGroupingSessionID: ObjectID!
		$columns: JSON
		$filters: [[FilterItem]]
		$sorts: [ReviewReportsSort]
		$summarizeBy: [ReviewReportsSummarizeBy]
		$aggregationOperations: [ReviewReportsAggregationOperation]
	) {
		reviewReportsCount(
			questionGroupingSessionID: $questionGroupingSessionID
			columns: $columns
			filters: $filters
			sorts: $sorts
			summarizeBy: $summarizeBy
			aggregationOperations: $aggregationOperations
		)
	}
`;

export const GET_RAW_REVIEW_REPORT_DATA_CSV = gql`
	query GetReviewReportDataCSV(
		$questionGroupingSessionID: ObjectID!
		$columns: JSON
		$pageNumber: String
		$pageSize: String
		$filters: [[FilterItem]]
		$sorts: [ReviewReportsSort]
		$reportName: String
		$summarizeBy: [ReviewReportsSummarizeBy]
		$aggregationOperations: [ReviewReportsAggregationOperation]
		$isSummarizeRowsView: Boolean
		$summaryColumnMetadata: JSON
		$summarizeByMetadata: JSON
	) {
		reviewReportsCSV(
			questionGroupingSessionID: $questionGroupingSessionID
			columns: $columns
			filters: $filters
			pageNumber: $pageNumber
			pageSize: $pageSize
			sorts: $sorts
			reportName: $reportName
			summarizeBy: $summarizeBy
			aggregationOperations: $aggregationOperations
			isSummarizeRowsView: $isSummarizeRowsView
			summaryColumnMetadata: $summaryColumnMetadata
			summarizeByMetadata: $summarizeByMetadata
		)
	}
`;

export const GET_REPORT_CSV_BY_ID = gql`
	query GetReviewReportCSVByID($reportID: ObjectID!) {
		reviewReportsCSVByID(reportID: $reportID)
	}
`;

export const GET_REVIEW_REPORT_DISTRIBUTION_DATA = gql`
	query GetReviewReportDistributionData(
		$questionGroupingSessionID: ObjectID!
		$columns: JSON
		$filters: [[FilterItem]]
		$summarizeBy: [ReviewReportsSummarizeBy]
		$bucketsNum: String
		$selectedColumnAggregationOperation: ReviewReportsAggregationOperation
		$min: String
		$max: String
	) {
		reviewReportsDistribution(
			questionGroupingSessionID: $questionGroupingSessionID
			columns: $columns
			filters: $filters
			summarizeBy: $summarizeBy
			bucketsNum: $bucketsNum
			selectedColumnAggregationOperation: $selectedColumnAggregationOperation
			min: $min
			max: $max
		)
	}
`;

export const GET_GOAL_REPORT_DISTRIBUTION_DATA = gql`
	query GetGoalReportDistributionData(
		$questionGroupingSessionID: ObjectID!
		$columns: JSON
		$filters: [[FilterItem]]
		$summarizeBy: [GoalReportsSummarizeBy]
		$bucketsNum: String
		$selectedColumnAggregationOperation: GoalReportsAggregationOperation
		$min: String
		$max: String
	) {
		goalReportsDistribution(
			questionGroupingSessionID: $questionGroupingSessionID
			columns: $columns
			filters: $filters
			summarizeBy: $summarizeBy
			bucketsNum: $bucketsNum
			selectedColumnAggregationOperation: $selectedColumnAggregationOperation
			min: $min
			max: $max
		)
	}
`;

export const GET_SURVEY_REPORT_DISTRIBUTION_DATA = gql`
	query GetSurveyReportDistributionData(
		$questionGroupingSessionID: ObjectID!
		$columns: JSON
		$filters: [[FilterItem]]
		$summarizeBy: [SurveyReportsSummarizeBy]
		$bucketsNum: String
		$selectedColumnAggregationOperation: SurveyReportsAggregationOperation
		$min: String
		$max: String
	) {
		surveyReportsDistribution(
			questionGroupingSessionID: $questionGroupingSessionID
			columns: $columns
			filters: $filters
			summarizeBy: $summarizeBy
			bucketsNum: $bucketsNum
			selectedColumnAggregationOperation: $selectedColumnAggregationOperation
			min: $min
			max: $max
		)
	}
`;

export const GET_EMPLOYEE_REPORT_DISTRIBUTION_DATA = gql`
	query GetEmployeeReportDistributionData(
		$columns: JSON
		$filters: [[FilterItem]]
		$summarizeBy: [EmployeeReportsSummarizeBy]
		$bucketsNum: String
		$selectedColumnAggregationOperation: EmployeeReportsAggregationOperation
		$min: String
		$max: String
	) {
		employeeReportsDistribution(
			columns: $columns
			filters: $filters
			summarizeBy: $summarizeBy
			bucketsNum: $bucketsNum
			selectedColumnAggregationOperation: $selectedColumnAggregationOperation
			min: $min
			max: $max
		)
	}
`;

// mutations

export const ADD_QUESTION_GROUPINGS = gql`
	mutation AddQuestionGroups(
		$questionGroupingSessionID: ObjectID!
		$newGroups: [QuestionGrouping]!
		$filters: [[FilterItem]]
		$reportType: String
	) {
		addQuestionGroupings(
			questionGroupingSessionID: $questionGroupingSessionID
			newGroups: $newGroups
			filters: $filters
			reportType: $reportType
		)
	}
`;

export const EDIT_QUESTION_GROUPINGS = gql`
	mutation EditQuestionGroupings(
		$questionGroupingSessionID: ObjectID!
		$groupingUpdates: [GroupingUpdate]!
		$filters: [[FilterItem]]
		$reportType: String
	) {
		editQuestionGroupings(
			questionGroupingSessionID: $questionGroupingSessionID
			groupingUpdates: $groupingUpdates
			filters: $filters
			reportType: $reportType
		)
	}
`;

export const DELETE_QUESTION_GROUPINGS = gql`
	mutation RemoveQuestionGroupings($questionGroupingSessionID: ObjectID!, $groupIDs: [String]!) {
		removeQuestionGroupings(questionGroupingSessionID: $questionGroupingSessionID, groupIDs: $groupIDs)
	}
`;

export const SAVE_REPORT_QUERY = gql`
	mutation CreateSavedReviewReportQuery(
		$employeeQueryID: ObjectID
		$savedQuery: SavedQueryInput
		$selectedColumns: JSON
		$columnMetadata: JSON
		$quickFilter: [QuickFilter]
		$sorts: [ReviewReportsSort]
		$summarizeBy: [String]
		$aggregationOperations: JSON
		$isSummarizeRowsView: Boolean
		$showAllAggregationColumns: Boolean
		$summaryColumnMetadata: JSON
		$questionGroupingSessionID: ObjectID
		$summarizeByMetadata: JSON
		$visualizeOptions: JSON
		$pageSize: Int
		$createdVia: String
	) {
		createSavedReviewReportQuery(
			employeeQueryID: $employeeQueryID
			savedQuery: $savedQuery
			selectedColumns: $selectedColumns
			columnMetadata: $columnMetadata
			quickFilter: $quickFilter
			sorts: $sorts
			summarizeBy: $summarizeBy
			aggregationOperations: $aggregationOperations
			isSummarizeRowsView: $isSummarizeRowsView
			showAllAggregationColumns: $showAllAggregationColumns
			summaryColumnMetadata: $summaryColumnMetadata
			questionGroupingSessionID: $questionGroupingSessionID
			summarizeByMetadata: $summarizeByMetadata
			visualizeOptions: $visualizeOptions
			pageSize: $pageSize
			createdVia: $createdVia
		) {
			savedReviewReportQueryID
			questionGroupingSessionID
		}
	}
`;

export const UPDATE_SAVED_REPORT_QUERY = gql`
	mutation UpdateSavedReviewReportQuery(
		$queryID: ObjectID
		$savedQuery: SavedQueryInput
		$selectedColumns: JSON
		$columnMetadata: JSON
		$quickFilter: [QuickFilter]
		$sorts: [ReviewReportsSort]
		$summarizeBy: [String]
		$aggregationOperations: JSON
		$isSummarizeRowsView: Boolean
		$showAllAggregationColumns: Boolean
		$summaryColumnMetadata: JSON
		$summarizeByMetadata: JSON
		$visualizeOptions: JSON
		$pageSize: Int
		$sharedWith: [String]
	) {
		updateSavedReviewReportQuery(
			queryID: $queryID
			savedQuery: $savedQuery
			selectedColumns: $selectedColumns
			columnMetadata: $columnMetadata
			quickFilter: $quickFilter
			sorts: $sorts
			summarizeBy: $summarizeBy
			aggregationOperations: $aggregationOperations
			isSummarizeRowsView: $isSummarizeRowsView
			showAllAggregationColumns: $showAllAggregationColumns
			summaryColumnMetadata: $summaryColumnMetadata
			summarizeByMetadata: $summarizeByMetadata
			visualizeOptions: $visualizeOptions
			pageSize: $pageSize
			sharedWith: $sharedWith
		)
	}
`;

export const SET_SELECTED_QUESTIONS = gql`
	mutation SetSelectedQuestions($questionGroupingSessionID: ObjectID!, $selectedQuestions: [SelectedQuestionInput]) {
		setSelectedQuestions(
			questionGroupingSessionID: $questionGroupingSessionID
			selectedQuestions: $selectedQuestions
		)
	}
`;

export const DELETE_SAVED_QUERY = gql`
	mutation DeleteSavedQuery($reportID: ObjectID!) {
		deleteSavedQuery(reportID: $reportID)
	}
`;

// found during integration testing: this is wrong
// field should not have any subfields as it is type "String"
export const GET_SAVED_EMPLOYEE_QUERIES = gql`
	query Query {
		savedEmployeeQueries {
			_id
			name
			description
			type
			tags
			filters {
				field {
					value
					label
				}
				type
				comparison
				value
			}
			createdAt
			updatedAt
			createdBy {
				_id
				name
			}
		}
	}
`;

export const GET_RAW_EMPLOYEE_REPORT_DATA = gql`
	query GetEmployeeReportData(
		$columns: JSON
		$pageNumber: String
		$pageSize: String
		$filters: [[FilterItem]]
		$sorts: [ReviewReportsSort]
		$summarizeBy: [ReviewReportsSummarizeBy]
		$aggregationOperations: [ReviewReportsAggregationOperation]
	) {
		employeeReports(
			columns: $columns
			filters: $filters
			pageNumber: $pageNumber
			pageSize: $pageSize
			sorts: $sorts
			summarizeBy: $summarizeBy
			aggregationOperations: $aggregationOperations
		)
	}
`;

export const GET_RAW_EMPLOYEE_REPORT_DATA_CSV = gql`
	query GetEmployeeReportDataCSV(
		$columns: JSON
		$pageNumber: String
		$pageSize: String
		$filters: [[FilterItem]]
		$sorts: [EmployeeReportsSort]
		$reportName: String
		$summarizeBy: [EmployeeReportsSummarizeBy]
		$aggregationOperations: [EmployeeReportsAggregationOperation]
		$isSummarizeRowsView: Boolean
		$summaryColumnMetadata: JSON
		$summarizeByMetadata: JSON
	) {
		employeeReportsCSV(
			columns: $columns
			filters: $filters
			pageNumber: $pageNumber
			pageSize: $pageSize
			sorts: $sorts
			reportName: $reportName
			summarizeBy: $summarizeBy
			aggregationOperations: $aggregationOperations
			isSummarizeRowsView: $isSummarizeRowsView
			summaryColumnMetadata: $summaryColumnMetadata
			summarizeByMetadata: $summarizeByMetadata
		)
	}
`;

export const GET_RAW_GOAL_REPORT_DATA = gql`
	query GetGoalReportData(
		$columns: JSON
		$pageNumber: String
		$pageSize: String
		$filters: [[FilterItem]]
		$sorts: [GoalReportsSort]
		$summarizeBy: [GoalReportsSummarizeBy]
		$aggregationOperations: [GoalReportsAggregationOperation]
	) {
		goalReports(
			columns: $columns
			filters: $filters
			pageNumber: $pageNumber
			pageSize: $pageSize
			sorts: $sorts
			summarizeBy: $summarizeBy
			aggregationOperations: $aggregationOperations
		)
	}
`;

export const GET_RAW_SURVEY_REPORT_DATA = gql`
	query GetSurveyReportData(
		$columns: JSON
		$questionGroupingSessionID: ObjectID!
		$pageNumber: String
		$pageSize: String
		$filters: [[FilterItem]]
		$sorts: [SurveyReportsSort]
		$summarizeBy: [SurveyReportsSummarizeBy]
		$aggregationOperations: [SurveyReportsAggregationOperation]
	) {
		surveyReports(
			columns: $columns
			questionGroupingSessionID: $questionGroupingSessionID
			filters: $filters
			pageNumber: $pageNumber
			pageSize: $pageSize
			sorts: $sorts
			summarizeBy: $summarizeBy
			aggregationOperations: $aggregationOperations
		)
	}
`;

export const GET_SURVEY_REPORT_DATA_COUNT = gql`
	query GetSurveyReportDataCount(
		$columns: JSON
		$filters: [[FilterItem]]
		$sorts: [SurveyReportsSort]
		$summarizeBy: [SurveyReportsSummarizeBy]
		$aggregationOperations: [SurveyReportsAggregationOperation]
		$questionGroupingSessionID: ObjectID!
	) {
		surveyReportsCount(
			columns: $columns
			filters: $filters
			sorts: $sorts
			summarizeBy: $summarizeBy
			aggregationOperations: $aggregationOperations
			questionGroupingSessionID: $questionGroupingSessionID
		)
	}
`;

export const GET_RAW_GOAL_REPORT_DATA_CSV = gql`
	query GetGoalReportDataCSV(
		$columns: JSON
		$pageNumber: String
		$pageSize: String
		$filters: [[FilterItem]]
		$sorts: [GoalReportsSort]
		$reportName: String
		$summarizeBy: [GoalReportsSummarizeBy]
		$aggregationOperations: [GoalReportsAggregationOperation]
		$isSummarizeRowsView: Boolean
		$summaryColumnMetadata: JSON
		$summarizeByMetadata: JSON
		$showAllAggregationColumns: Boolean
	) {
		goalReportsCSV(
			columns: $columns
			filters: $filters
			pageNumber: $pageNumber
			pageSize: $pageSize
			sorts: $sorts
			reportName: $reportName
			summarizeBy: $summarizeBy
			aggregationOperations: $aggregationOperations
			isSummarizeRowsView: $isSummarizeRowsView
			summaryColumnMetadata: $summaryColumnMetadata
			summarizeByMetadata: $summarizeByMetadata
			showAllAggregationColumns: $showAllAggregationColumns
		)
	}
`;

export const GET_RAW_SURVEY_REPORT_DATA_CSV = gql`
	query GetSurveyReportDataCSV(
		$columns: JSON
		$pageNumber: String
		$pageSize: String
		$filters: [[FilterItem]]
		$sorts: [GoalReportsSort]
		$reportName: String
		$questionGroupingSessionID: ObjectID
		$summarizeBy: [GoalReportsSummarizeBy]
		$aggregationOperations: [GoalReportsAggregationOperation]
		$isSummarizeRowsView: Boolean
		$summaryColumnMetadata: JSON
		$summarizeByMetadata: JSON
		$showAllAggregationColumns: Boolean
	) {
		surveyReportsCSV(
			columns: $columns
			filters: $filters
			pageNumber: $pageNumber
			pageSize: $pageSize
			sorts: $sorts
			reportName: $reportName
			questionGroupingSessionID: $questionGroupingSessionID
			summarizeBy: $summarizeBy
			aggregationOperations: $aggregationOperations
			isSummarizeRowsView: $isSummarizeRowsView
			summaryColumnMetadata: $summaryColumnMetadata
			summarizeByMetadata: $summarizeByMetadata
			showAllAggregationColumns: $showAllAggregationColumns
		)
	}
`;

export const GET_SURVEY_NAMES = gql`
	query Query {
		uniqueSurveyNames {
			_id
			name
		}
	}
`;

/**
 * Queries below currently unused but valid. Left in for use when building out the Employee Report
 */
/*
export const SAVE_EMPLOYEE_QUERY = gql`
    mutation CreateSavedEmployeeQuery($savedQuery: SavedQueryInput) {
        createSavedEmployeeQuery(savedQuery: $savedQuery)
    }
`;

export const UPDATE_SAVED_EMPLOYEE_QUERY = gql`
    mutation UpdateSavedEmployeeQuery($queryID: ObjectID, $savedQuery: SavedQueryInput, $selectedColumns: JSON){
        updateSavedEmployeeQuery(queryID: $queryID, savedQuery: $savedQuery, selectedColumns: $selectedColumns)
    }
`;

export const GET_CHAIN_OF_COMMAND = gql`
    query Query($employeeID: ObjectID!) {
        chainOfCommand(employeeID: $employeeID) {
                _id,
                name,
                email
        }
    }
`;
*/

const includeQuestionGroupingSessionID = (reportsType: string) =>
	reportsType === "reviewReports" || reportsType === "surveyReports";

export const GET_RAW_REPORT_DATA = (
	queryName: string,
	reportsType: "reviewReports" | "employeeReports" | "goalReports" | "surveyReports",
	sorts,
	summarizeBy,
	aggregationOperations
) => gql`
    query ${queryName}(
        ${includeQuestionGroupingSessionID(reportsType) ? "$questionGroupingSessionID: ObjectID!\n        " : ""}$columns: JSON
        $pageNumber: String
        $pageSize: String
        $filters: [[FilterItem]]
        $sorts: [${sorts}]
        $summarizeBy: [${summarizeBy}]
        $aggregationOperations: [${aggregationOperations}]
    ) {
        ${reportsType}(
            ${includeQuestionGroupingSessionID(reportsType) ? "questionGroupingSessionID: $questionGroupingSessionID\n            " : ""}columns: $columns
            filters: $filters
            pageNumber: $pageNumber
            pageSize: $pageSize
            sorts: $sorts
            summarizeBy: $summarizeBy
            aggregationOperations: $aggregationOperations
        )
    }
`;
